import type { IntlShape } from 'react-intl';
import type { PatientsListQueryParams } from '~/backend/connector-next-api/connector-next-components';
import { COURSE_STAGE_VALUES, courseStageLabelId } from './patientListModel';
import type { CatalogMessageId } from '~/i18n';

/**
 * Wire value unions of the three filter query parameters, from the generated
 * client. The connector spec declares the enum vocabularies, so a backend
 * enum change regenerates these unions and fails compilation at the option
 * tables below instead of silently leaving the menu stale.
 */
type StatusWire = NonNullable<PatientsListQueryParams['status']>[number];
type CourseStageWire = NonNullable<
    PatientsListQueryParams['courseStage']
>[number];
type ModalityWire = NonNullable<PatientsListQueryParams['modality']>[number];

/**
 * The multi-valued filter dimensions; empty array means unfiltered. Course
 * stage, status and modality are wire-typed, so they can only hold what the
 * connector accepts.
 */
type PatientFilterFacets = {
    /** The stage the user has put the patient's course at. */
    courseStage: readonly CourseStageWire[];
    /** Patient lifecycle status (Active / Archived). */
    status: readonly StatusWire[];
    /** Image modality the patient holds at least one series of. */
    modality: readonly ModalityWire[];
    /** Id of the treatment site chosen for the patient. */
    site: readonly string[];
    /** Id of a clinical tag the patient carries. */
    tag: readonly string[];
};

/** Every filter dimension. Evaluated server-side so the reported total matches. */
export type PatientFilters = PatientFilterFacets & {
    /** Access user the patient must be assigned to; undefined for any assignee. */
    assignedToUserId: string | undefined;
};

export type PatientFilterFacetId = keyof PatientFilterFacets;

export const NO_PATIENT_FILTERS: PatientFilters = {
    courseStage: [],
    status: [],
    modality: [],
    site: [],
    tag: [],
    assignedToUserId: undefined,
};

export type PatientFilterOption<V extends string = string> = {
    /** Connector enum member name or clinical list id, sent verbatim. */
    value: V;
    /** Option label message id; takes precedence over `label`. */
    labelId?: CatalogMessageId;
    /**
     * Literal label, for a clinical list entry named by an administrator. With
     * neither, the wire value is shown as-is (matches the row chips for
     * modality).
     */
    label?: string;
};

export type PatientFilterFacet = {
    id: PatientFilterFacetId;
    labelId: CatalogMessageId;
    /**
     * "{count} statuses" - the chip's value segment once more than one is
     * selected. Absent for a dimension offered only alongside others, whose
     * control counts its selections as one.
     */
    countLabelId?: CatalogMessageId;
    options: readonly PatientFilterOption[];
};

/**
 * Label id per lifecycle status. Keyed by the wire union, so every connector
 * value must be handled here by name.
 */
const PATIENT_STATUS_LABELS = {
    Active: 'patientList.filter.status.active',
    Archived: 'patientList.filter.status.archived',
} satisfies Record<StatusWire, CatalogMessageId>;

const PATIENT_STATUS_OPTIONS: readonly PatientFilterOption<StatusWire>[] = (
    Object.keys(PATIENT_STATUS_LABELS) as StatusWire[]
).map((value) => ({ value, labelId: PATIENT_STATUS_LABELS[value] }));

/**
 * Label id (or none: the code labels itself, matching the row chips) per
 * image modality, in menu order with the unparsed-modality value (Unknown, a
 * real stored value) last. Keyed by the wire union; `PET` is the connector's
 * name for DICOM's `PT`.
 */
const MODALITY_LABELS = {
    CT: undefined,
    MR: undefined,
    PET: undefined,
    CBCT: undefined,
    Unknown: 'patientList.filter.modality.unknown',
} satisfies Record<ModalityWire, CatalogMessageId | undefined>;

const MODALITY_OPTIONS: readonly PatientFilterOption<ModalityWire>[] = (
    Object.keys(MODALITY_LABELS) as ModalityWire[]
).map((value) => ({ value, labelId: MODALITY_LABELS[value] }));

const COURSE_STAGE_OPTIONS: readonly PatientFilterOption<CourseStageWire>[] =
    COURSE_STAGE_VALUES.map((value) => ({
        value,
        labelId: courseStageLabelId(value),
    }));

/** The dimensions whose options the device defines, in filter bar order. */
const FIXED_FACETS = {
    courseStage: {
        id: 'courseStage',
        labelId: 'patientList.filter.facet.courseStage',
        countLabelId: 'patientList.filter.count.courseStage',
        options: COURSE_STAGE_OPTIONS,
    },
    modality: {
        id: 'modality',
        labelId: 'patientList.filter.facet.modality',
        options: MODALITY_OPTIONS,
    },
    status: {
        id: 'status',
        labelId: 'patientList.filter.facet.status',
        options: PATIENT_STATUS_OPTIONS,
    },
} as const satisfies Record<string, PatientFilterFacet>;

/** A clinical list entry as the filters and pickers read it. */
type NamedValue = {
    id?: string | null;
    name?: string | null;
    retired?: boolean;
};

/**
 * The clinical tags and sites the site and tag dimensions offer, retired ones
 * included; each undefined until it has loaded.
 */
export type ConfiguredFilterValues = {
    sites: readonly NamedValue[] | undefined;
    tags:
        | readonly (NamedValue & {
              color?: string | null;
              showAsFilter?: boolean;
          })[]
        | undefined;
};

/** A clinical list entry as an option: its id, under its name. */
type NamedOption = { value: string; label: string };

/** Each entry with an id as an option under its name. */
export const configuredOptions = (
    values: readonly NamedValue[]
): NamedOption[] =>
    values.flatMap((value) =>
        value.id == null ? [] : [{ value: value.id, label: value.name ?? '…' }]
    );

/** The entries that can be newly chosen: those not retired. */
export const offeredEntries = <T extends NamedValue>(
    values: readonly T[]
): T[] => values.filter((value) => !value.retired);

/**
 * The `offered` values in order, followed by any `held` id they leave out, so
 * a held value can always be seen and lifted. A held id is named from `named`,
 * reads "…" while `named` has not loaded, and `unlisted` when no entry of it
 * has the id.
 */
export function offeredWithHeld(
    offered: readonly NamedValue[],
    held: readonly string[],
    named: readonly NamedValue[] | undefined,
    unlisted: string
): NamedOption[] {
    const options = configuredOptions(offered);
    const stranded = held
        .filter((id) => !options.some((option) => option.value === id))
        .map((id) => ({
            value: id,
            label:
                named === undefined
                    ? '…'
                    : (named.find((each) => each.id === id)?.name ?? unlisted),
        }));
    return [...options, ...stranded];
}

/**
 * The tags the filter bar offers: the offered ones shown as a filter, in list
 * order, followed by any tag still selected that no longer is, which reads
 * `unlisted` when no tag has its id.
 */
export function filterTags(
    configured: ConfiguredFilterValues,
    filters: PatientFilters,
    unlisted: string
): PatientFilterOption[] {
    return offeredWithHeld(
        offeredEntries(configured.tags ?? []).filter(
            (tag) => tag.showAsFilter === true
        ),
        filters.tag,
        configured.tags,
        unlisted
    );
}

/**
 * One control of the filter bar: a single dimension under its own name, or
 * several rarely narrowed dimensions together under one name.
 */
export type PatientFilterGroup = {
    id: string;
    /** The control's name; a single dimension's own name when undefined. */
    labelId?: CatalogMessageId;
    facets: readonly PatientFilterFacet[];
};

/**
 * The filter bar's dimension controls, in order: course stage, treatment site,
 * and the patient's modality and status together under "More". The site
 * control offers the offered sites and any site still selected, which reads
 * `unlistedSite` when no site has its id, and is left out while it has none.
 * Clinical tags are not a dimension control: the tags shown as filters are
 * toggles of their own.
 */
export function patientFilterGroups(
    configured: ConfiguredFilterValues,
    filters: PatientFilters,
    unlistedSite: string
): PatientFilterGroup[] {
    const site: PatientFilterFacet = {
        id: 'site',
        labelId: 'patientList.filter.facet.site',
        countLabelId: 'patientList.filter.count.site',
        options: offeredWithHeld(
            offeredEntries(configured.sites ?? []),
            filters.site,
            configured.sites,
            unlistedSite
        ),
    };
    const groups: PatientFilterGroup[] = [
        { id: 'courseStage', facets: [FIXED_FACETS.courseStage] },
        { id: 'site', facets: [site] },
        {
            id: 'more',
            labelId: 'patientList.filter.more',
            facets: [FIXED_FACETS.modality, FIXED_FACETS.status],
        },
    ];
    return groups.filter((group) =>
        group.facets.every((facet) => facet.options.length > 0)
    );
}

/**
 * Display label of one option: its message, its configured name, or the wire
 * value when it is its own label.
 */
export function filterOptionLabel(
    intl: IntlShape,
    option: PatientFilterOption
): string {
    if (option.labelId !== undefined) {
        return intl.formatMessage({ id: option.labelId });
    }
    return option.label ?? option.value;
}

/** Whether `value` is currently selected in `facetId`. */
export function isFilterSelected(
    filters: PatientFilters,
    facetId: PatientFilterFacetId,
    value: string
): boolean {
    const selected: readonly string[] = filters[facetId];
    return selected.includes(value);
}

/**
 * One facet's typed option values that `selected` names, in table order.
 * Rebuilding from the table is what keeps the state wire-valid: a value the
 * facet does not offer cannot survive into it.
 */
function selectFromTable<V extends string>(
    options: readonly PatientFilterOption<V>[],
    selected: ReadonlySet<string>
): readonly V[] {
    return options
        .map((option) => option.value)
        .filter((value) => selected.has(value));
}

/**
 * A configured dimension's selection, in ordinal order so the request is the
 * same whatever order the values were picked in.
 */
const sortedSelection = (selected: ReadonlySet<string>): readonly string[] =>
    [...selected].sort();

/** `filters` with `facetId` holding exactly the values in `selected`. */
function withFacetSelection(
    filters: PatientFilters,
    facetId: PatientFilterFacetId,
    selected: ReadonlySet<string>
): PatientFilters {
    return {
        courseStage:
            facetId === 'courseStage'
                ? selectFromTable(COURSE_STAGE_OPTIONS, selected)
                : filters.courseStage,
        status:
            facetId === 'status'
                ? selectFromTable(PATIENT_STATUS_OPTIONS, selected)
                : filters.status,
        modality:
            facetId === 'modality'
                ? selectFromTable(MODALITY_OPTIONS, selected)
                : filters.modality,
        site: facetId === 'site' ? sortedSelection(selected) : filters.site,
        tag: facetId === 'tag' ? sortedSelection(selected) : filters.tag,
        assignedToUserId: filters.assignedToUserId,
    };
}

/** `filters` narrowed to the patients assigned to the access user `userId`, or to any assignee when undefined. */
export function withAssignedToUserId(
    filters: PatientFilters,
    userId: string | undefined
): PatientFilters {
    return { ...filters, assignedToUserId: userId };
}

/**
 * `filters` with `value` toggled in `facetId`; a fixed dimension keeps its
 * declared order, not selection order.
 */
export function toggleFilterValue(
    filters: PatientFilters,
    facetId: PatientFilterFacetId,
    value: string
): PatientFilters {
    const selected = new Set<string>(filters[facetId]);
    if (!selected.delete(value)) {
        selected.add(value);
    }
    return withFacetSelection(filters, facetId, selected);
}

const FACET_IDS: readonly PatientFilterFacetId[] = [
    'courseStage',
    'status',
    'modality',
    'site',
    'tag',
];

/** Whether any dimension is filtered. */
export function hasActivePatientFilters(filters: PatientFilters): boolean {
    return (
        filters.assignedToUserId !== undefined ||
        FACET_IDS.some((id) => filters[id].length > 0)
    );
}

/** `filters` with `facetId` emptied; the other dimensions are untouched. */
export function clearFacet(
    filters: PatientFilters,
    facetId: PatientFilterFacetId
): PatientFilters {
    return withFacetSelection(filters, facetId, new Set());
}

/**
 * What a filter control says: its name alone while nothing is selected, then
 * the one value, or a count once several are; the full list is kept for the
 * control's tooltip and its accessible name.
 */
export function filterGroupSummary(
    intl: IntlShape,
    group: PatientFilterGroup,
    filters: PatientFilters
): {
    label: string;
    valueSummary: string | undefined;
    valueLabels: string[];
} {
    const valueLabels = group.facets.flatMap((facet) =>
        facet.options
            .filter((option) =>
                isFilterSelected(filters, facet.id, option.value)
            )
            .map((option) => filterOptionLabel(intl, option))
    );
    const [single] = group.facets;
    const label = intl.formatMessage({
        id: group.labelId ?? single.labelId,
    });
    if (valueLabels.length <= 1) {
        return { label, valueLabels, valueSummary: valueLabels[0] };
    }
    const countLabelId =
        group.facets.length === 1 ? single.countLabelId : undefined;
    return {
        label,
        valueLabels,
        valueSummary: intl.formatMessage(
            { id: countLabelId ?? 'patientList.filter.count.selected' },
            { count: valueLabels.length }
        ),
    };
}

/** `filters` with every dimension of `group` emptied; the others are untouched. */
export function clearGroup(
    filters: PatientFilters,
    group: PatientFilterGroup
): PatientFilters {
    return group.facets.reduce(
        (current, facet) => clearFacet(current, facet.id),
        filters
    );
}

type FilterQueryParamKey = PatientFilterFacetId | 'assignedToUserId';

/**
 * Connector query params for `filters`; an unselected dimension is
 * omitted, not sent empty, since the connector reads absent as unfiltered
 * and empty as a parse error. The Required source object makes adding a
 * dimension a compile error here until it is sent.
 */
export function patientFilterQueryParams(
    filters: PatientFilters
): Partial<Pick<PatientsListQueryParams, FilterQueryParamKey>> {
    const all: Required<Pick<PatientsListQueryParams, FilterQueryParamKey>> = {
        courseStage: [...filters.courseStage],
        status: [...filters.status],
        modality: [...filters.modality],
        site: [...filters.site],
        tag: [...filters.tag],
        assignedToUserId: filters.assignedToUserId ?? '',
    };
    const params: Partial<typeof all> = {};
    if (all.courseStage.length > 0) {
        params.courseStage = all.courseStage;
    }
    if (all.status.length > 0) {
        params.status = all.status;
    }
    if (all.modality.length > 0) {
        params.modality = all.modality;
    }
    if (all.site.length > 0) {
        params.site = all.site;
    }
    if (all.tag.length > 0) {
        params.tag = all.tag;
    }
    if (all.assignedToUserId !== '') {
        params.assignedToUserId = all.assignedToUserId;
    }
    return params;
}
